/* Vinyasa Automotive — animations.js
   Entrance + scroll-reveal motion for the storefront. Premium but restrained: quart-out
   easing, transform/opacity only, IntersectionObserver (no scroll listeners).

   WHY NO GSAP: this file used to drive GSAP + ScrollTrigger (112 KiB of library) to do four
   fade-and-rise reveals. Lighthouse mobile attributed 767 ms of main-thread blocking to
   gsap.min.js alone — the single largest scriptable cost on the page, for effects the
   platform does natively. The animations below are visually identical; the easing curve
   is easeOutQuart, which is what GSAP's "power3.out" resolves to.

   Progressive enhancement / accessibility contract (unchanged from the GSAP version, and
   the reason the hiding is done the way it is):
   - Nothing is hidden by CSS alone. The start-state only applies under `html.reveal-js`,
     a class set by the inline head script in theme.liquid — which ALSO arms a failsafe
     timer that reveals everything if this file never loads. Content can never get stuck
     invisible because a script 404'd.
   - prefers-reduced-motion: reduce → the head script never sets the class, so everything
     renders instantly with no observer work at all.
   - No IntersectionObserver support → same, instant render.
   - Reveals fire once and never re-hide. */
(function () {
  "use strict";

  var root = document.documentElement;
  if (!root.classList.contains("reveal-js")) return; // reduced-motion / unsupported → no-op

  /* Reveal everything and stand down. Used by the failsafe and on any internal error —
     "visible but unanimated" is always the correct degraded state. */
  function revealAll() {
    root.classList.add("reveal-all");
  }

  var STAGGER = {                     // seconds between siblings, per group
    ".hero__inner > *": 0.08,
    ".product-grid > *": 0.06,
    ".tile-grid > *": 0.06,
    ".trust__grid > *": 0.08
  };

  /* Below-the-fold groups: [selector, rootMargin]. The margin mirrors the old
     ScrollTrigger start values ("top 85%" ≈ fire when the element is 15% up the
     viewport, i.e. a -15% bottom margin). */
  var SCROLL_GROUPS = [
    [".section .section-head", "0px 0px -15% 0px"],
    [".product-grid > *", "0px 0px -10% 0px"],
    [".tile-grid > *", "0px 0px -10% 0px"],
    [".trust__grid > *", "0px 0px -12% 0px"]
  ];

  function staggerFor(sel) {
    return STAGGER[sel] || 0;
  }

  function init() {
    try {
      /* ---- Hero entrance: above the fold, so it plays on load, not on scroll ----
         sections/hero.liquid now starts it inline (the moment the hero is parsed, long before
         this deferred file runs on a slow phone); this stays as the path for a hero that was
         not revealed inline. Re-adding `is-in` is harmless either way. */
      var heroBits = document.querySelectorAll(".hero__inner > *:not(.is-in)");
      Array.prototype.forEach.call(heroBits, function (el, i) {
        el.style.setProperty("--rd", (i * STAGGER[".hero__inner > *"]).toFixed(2) + "s");
        el.classList.add("is-in");
      });
      var heroImg = document.querySelector(".hero__media img");
      if (heroImg) heroImg.classList.add("is-in");

      /* ---- Scroll reveals ----
         One observer per group so each can carry its own rootMargin. Items are unobserved
         the moment they fire, so the observer set drains as the user scrolls. */
      SCROLL_GROUPS.forEach(function (group) {
        var sel = group[0];
        var items = document.querySelectorAll(sel);
        if (!items.length) return;

        var step = staggerFor(sel);
        var obs = new IntersectionObserver(function (entries, self) {
          /* Stagger within the batch that became visible together, matching
             ScrollTrigger.batch: index within THIS batch, not within the document. */
          var shown = 0;
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.style.setProperty("--rd", (shown * step).toFixed(2) + "s");
            entry.target.classList.add("is-in");
            shown++;
            self.unobserve(entry.target);
          });
        }, { rootMargin: group[1], threshold: 0 });

        Array.prototype.forEach.call(items, function (el) { obs.observe(el); });
      });
    } catch (e) {
      revealAll();
      if (window.console && console.warn) console.warn("animations.js: setup failed, revealed all", e);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
